const getGradientColor = (start, end, index, total) => {
  const mix = index / Math.max(total - 1, 1);

  const hexToRgb = hex => {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  };

  const rgbToHex = ([r, g, b]) =>
    `#${[r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')}`;

  const s = hexToRgb(start);
  const e = hexToRgb(end);

  return rgbToHex([
    Math.round(s[0] + (e[0] - s[0]) * mix),
    Math.round(s[1] + (e[1] - s[1]) * mix),
    Math.round(s[2] + (e[2] - s[2]) * mix),
  ]);
};


export const transformTreeMapData = (data) => {
  if (!data || !data.hierarchy || !data.rows) return [];

  const metricMap = buildMetricMap(data);
  const sanitizedHierarchy = sanitizeHierarchyForTreemap(data.hierarchy);

  return buildTreemapNodes(sanitizedHierarchy, metricMap);
};


const buildMetricMap = ({ rows, metric_data, metric_count }) => {
  const map = new Map();
  rows.forEach((row, i) => {
    map.set(row, {
      value: metric_count?.[i]?.[0] ?? 0,
      positivePct: metric_data?.[i]?.[1] ?? 0,
      negativePct: metric_data?.[i]?.[2] ?? 0,
      positiveCnt: metric_count?.[i]?.[1] ?? 0,
      negativeCnt: metric_count?.[i]?.[2] ?? 0
    });
  });

  return map;
};


const sanitizeHierarchyForTreemap = (hierarchy) => {
  const seen = new Set();
  const sanitized = [];

  hierarchy.forEach(item => {
    const { topic, parent_topic, level } = item || {};

    if (!topic || !level) return;

    if (topic === parent_topic) return;

    const key = `${topic}::${parent_topic || ''}`;
    if (seen.has(key)) return;

    seen.add(key);

    sanitized.push({
      topic,
      parent_topic: parent_topic || null,
      level
    });
  });

  return sanitized;
};


const buildTreemapNodes = (hierarchy, metricMap) => {
  const treemapData = [];

  const l1Topics = hierarchy.filter(h => h.level === 'l1');
  const l2Topics = hierarchy.filter(h => h.level === 'l2');

  const l1ToChildren = {};
  l2Topics.forEach(h => {
    l1ToChildren[h.parent_topic] ??= [];
    l1ToChildren[h.parent_topic].push(h.topic);
  });

  l1Topics.forEach(l1 => {
    const l1Metrics = metricMap.get(l1.topic) || {};

    treemapData.push({
      id: l1.topic,
      parent: '',
      name: l1.topic,
      color: getTopicColor(l1.topic),
      custom: l1Metrics
    });

    const children = l1ToChildren[l1.topic] || [];

    const sortedChildren = children
      .map(topic => ({
        topic,
        value: metricMap.get(topic)?.value ?? 0
      }))
      .sort((a, b) => b.value - a.value);

    sortedChildren.forEach((item, index) => {
      const metrics = metricMap.get(item.topic) || {};

      treemapData.push({
        id: item.topic,
        parent: l1.topic,
        name: item.topic.split(' - ').pop(),
        value: Math.max(item.value, 1),
        color: getGradientColor(
          '#e26067',
          '#5ec871',
          index,
          sortedChildren.length
        ),
        custom: metrics
      });
    });
  });

  return treemapData;
};


const getTopicColor = (topic) => {
  const palette = [
    '#93c5fd',
    '#a5b4fc',
    '#c7d2fe',
    '#bae6fd',
    '#bfdbfe'
  ];

  let hash = 0;
  for (let i = 0; i < topic.length; i++) {
    hash = topic.charCodeAt(i) + ((hash << 5) - hash);
  }

  return palette[Math.abs(hash) % palette.length];
};